import { Eye, EyeOff } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useAuthStore } from '../auth/useAuthStore';
import { usePortalAccess } from './PortalAccessContext';

const CYAN = '#1B83A6';
const BORDER_IDLE = 'rgba(255, 255, 255, 0.15)';

const LABEL_STYLE = {
  display: 'block',
  color: 'rgba(255, 255, 255, 0.65)',
  fontSize: '0.75rem',
  marginBottom: '0.35rem',
  fontWeight: 500,
} as const;

const INPUT_BASE_STYLE = {
  width: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  border: `1px solid ${BORDER_IDLE}`,
  borderRadius: '7px',
  color: 'white',
  fontSize: '0.85rem',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  boxSizing: 'border-box' as const,
};

// ─── Fondo — réplica exacta del login de Transit Suite ─────────────────────

function PortalLoginBackground(): JSX.Element {
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: '-10px',
          backgroundImage: 'url(/assets/brand/bahia-blanca-login-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 35%',
          filter: 'blur(1.5px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, transparent 25%, rgba(8, 18, 28, 0.72) 100%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backdropFilter: 'blur(0.5px)',
          WebkitBackdropFilter: 'blur(0.5px)',
          backgroundColor: 'rgba(8, 18, 28, 0.15)',
        }}
      />
    </>
  );
}

// ─── Campo contraseña con toggle mostrar/ocultar ────────────────────────────

interface PasswordFieldProps {
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
}

function PortalPasswordField({ value, disabled, onChange }: PasswordFieldProps): JSX.Element {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const shouldShowEyeButton = value.length > 0 || focused;

  return (
    <div style={{ position: 'relative' }}>
      <input
        id="portal-password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        required
        disabled={disabled}
        placeholder="••••••••"
        autoComplete="current-password"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ ...INPUT_BASE_STYLE, padding: '0.5rem 2.5rem 0.5rem 0.75rem' }}
        onFocus={e => {
          e.target.style.borderColor = CYAN;
          setFocused(true);
        }}
        onBlur={e => {
          e.target.style.borderColor = BORDER_IDLE;
          setFocused(false);
        }}
      />
      {shouldShowEyeButton && (
        <button
          type="button"
          onClick={() => setShowPassword(v => !v)}
          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          style={{
            position: 'absolute',
            right: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#9CA3AF',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = '#cbd5e1')}
          onMouseLeave={e => (e.currentTarget.style.color = '#9CA3AF')}
        >
          {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
        </button>
      )}
    </div>
  );
}

// ─── Formulario ──────────────────────────────────────────────────────────────

interface PortalLoginFormProps {
  email: string;
  password: string;
  isLoading: boolean;
  error: string | null;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

function PortalLoginForm({
  email,
  password,
  isLoading,
  error,
  onEmailChange,
  onPasswordChange,
  onSubmit,
}: PortalLoginFormProps): JSX.Element {
  return (
    <form onSubmit={onSubmit} style={{ width: '100%' }}>
      <div style={{ marginBottom: '1rem' }}>
        <label htmlFor="portal-email" style={LABEL_STYLE}>
          Email
        </label>
        <input
          id="portal-email"
          name="email"
          type="email"
          required
          disabled={isLoading}
          placeholder="correo@ejemplo.com"
          autoComplete="email"
          value={email}
          onChange={e => onEmailChange(e.target.value)}
          style={{ ...INPUT_BASE_STYLE, padding: '0.5rem 0.75rem' }}
          onFocus={e => (e.target.style.borderColor = CYAN)}
          onBlur={e => (e.target.style.borderColor = BORDER_IDLE)}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label htmlFor="portal-password" style={LABEL_STYLE}>
          Contraseña
        </label>
        <PortalPasswordField value={password} disabled={isLoading} onChange={onPasswordChange} />
      </div>

      {error && (
        <div
          role="alert"
          style={{
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            border: '1px solid rgba(255, 107, 107, 0.3)',
            borderRadius: '6px',
            padding: '0.5rem 0.75rem',
            marginBottom: '1rem',
            color: '#ff6b6b',
            fontSize: '0.75rem',
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        style={{
          width: '100%',
          padding: '0.6rem',
          backgroundColor: isLoading ? 'rgba(27,131,166,0.5)' : CYAN,
          color: '#ffffff',
          border: 'none',
          borderRadius: '7px',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: isLoading ? 'none' : '0 4px 20px rgba(27,131,166,0.35)',
          letterSpacing: '0.03em',
        }}
        onMouseEnter={e => {
          if (!isLoading) e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={e => {
          if (!isLoading) e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {isLoading ? 'Ingresando…' : 'Ingresar'}
      </button>
    </form>
  );
}

// ─── Página principal ────────────────────────────────────────────────────────

/**
 * Login propio del portal de cliente. Reusa `useAuthStore().login()` —
 * mismo backend, mismo JWT, misma sesión — sin ningún flujo de
 * autenticación paralelo. Réplica deliberada, a pedido, del layout/estilo
 * visual real del login de Transit Suite (`Suite/frontend/src/core/auth/
 * Login.jsx`) — panel de vidrio a la izquierda, imagen de fondo, viñeta
 * radial — con la marca e imagen copiadas a `public/assets/brand/`.
 */
export function ClientPortalLoginPage(): JSX.Element {
  const { config } = usePortalAccess();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    clearError();
    void login({ email, password }).catch(() => {
      // Error ya expuesto por el store (useAuthStore().error).
    });
  };

  return (
    <div
      style={{
        position: 'relative',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: '#0C1B28',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <PortalLoginBackground />

      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '34.5%',
          backgroundColor: 'rgba(10, 20, 32, 0.52)',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem 3.5rem',
          gap: '2rem',
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '340px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <img
              src="/assets/brand/sintax-logo-negativo.svg"
              alt="Sintax"
              style={{ width: '210px', height: 'auto', marginLeft: '-33px' }}
            />
            <div
              style={{
                color: 'rgba(255, 255, 255, 0.75)',
                fontSize: '0.9rem',
                fontWeight: 600,
                marginTop: '0.5rem',
              }}
            >
              {config?.title ?? 'Portal SINTAX'}
            </div>
          </div>

          <PortalLoginForm
            email={email}
            password={password}
            isLoading={isLoading}
            error={error}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}
